import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';

/**
 * Picker mode for the calendar display
 */
export type PickerMode = 'day' | 'week' | 'month' | 'quarter' | 'year';

/**
 * Props for the DateRangePicker component
 */
export interface DateRangePickerProps {
    /** The start date of the range */
    rangeStart?: Date;
    /** The end date of the range */
    rangeEnd?: Date;
    /** Callback fired when a date is selected. In week mode, endDate is also provided. */
    onSelect?: (date: Date, endDate?: Date) => void;
    /** Function to determine if a date should be disabled */
    disabled?: (date: Date) => boolean;
    /** Additional CSS classes to apply to the calendar */
    className?: string;
    /** The default month to display when no date is selected */
    defaultMonth?: Date;
    /** Controlled month - when provided, the calendar will jump to this month */
    month?: Date;
    /** The picker mode - determines how dates are displayed and selected */
    mode?: PickerMode;
    /** Minimum selectable date */
    minDate?: Date;
    /** Maximum selectable date */
    maxDate?: Date;
}

/**
 * Gets the number of days in a given month
 */
const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
};

/**
 * Gets the day of the week for the first day of a given month
 */
const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
};

/**
 * Checks if two dates are the same day
 */
const isSameDay = (date1: Date, date2: Date) => {
    return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
    );
};

/**
 * Checks if a date is within a range (inclusive)
 */
const isInRange = (date: Date, start?: Date, end?: Date) => {
    if (!start || !end) return false;
    const dateTime = date.getTime();
    const startTime = start.getTime();
    const endTime = end.getTime();
    return dateTime > startTime && dateTime < endTime;
};

/**
 * Checks if a date is the range start
 */
const isRangeStart = (date: Date, start?: Date) => {
    if (!start) return false;
    return isSameDay(date, start);
};

/**
 * Checks if a date is the range end
 */
const isRangeEnd = (date: Date, end?: Date) => {
    if (!end) return false;
    return isSameDay(date, end);
};

/**
 * Checks if a date is today
 */
const isToday = (date: Date) => {
    const today = new Date();
    return isSameDay(date, today);
};

/**
 * Gets the start of the week (Sunday) for a given date
 */
const getWeekStart = (date: Date) => {
    const result = new Date(date);
    const day = result.getDay();
    result.setDate(result.getDate() - day);
    return result;
};

/**
 * Gets the end of the week (Saturday) for a given date
 */
const getWeekEnd = (date: Date) => {
    const result = new Date(date);
    const day = result.getDay();
    result.setDate(result.getDate() + (6 - day));
    return result;
};

/**
 * Checks if two dates are in the same week
 */
const isSameWeek = (date1: Date, date2: Date) => {
    const week1Start = getWeekStart(date1);
    const week2Start = getWeekStart(date2);
    return isSameDay(week1Start, week2Start);
};

/**
 * Gets the quarter number (1-4) for a given month (0-11)
 */
const getQuarter = (month: number) => Math.floor(month / 3) + 1;

/**
 * Quarter labels
 */
const QUARTER_LABELS = ['Q1', 'Q2', 'Q3', 'Q4'];

/**
 * Month labels (short form)
 */
const MONTH_LABELS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];

/**
 * A date range picker component supporting multiple picker modes.
 *
 * Features:
 * - Range selection with visual highlighting
 * - Day, Week, Month, Quarter, and Year picker modes
 * - Proper date range highlighting with ArchiPro brand colours
 * - Disabled date support
 *
 * @example
 * ```tsx
 * <DateRangePicker
 *   rangeStart={startDate}
 *   rangeEnd={endDate}
 *   onSelect={handleDateSelect}
 *   mode="day"
 * />
 * ```
 */
export const DateRangePicker = React.forwardRef<
    HTMLDivElement,
    DateRangePickerProps
>(
    (
        {
            rangeStart,
            rangeEnd,
            onSelect,
            disabled,
            className,
            defaultMonth,
            month,
            mode = 'day',
        },
        ref,
    ) => {
        const [currentView, setCurrentView] = React.useState(
            month || rangeStart || defaultMonth || new Date(),
        );

        // Sync currentView when controlled month prop changes
        React.useEffect(() => {
            if (month) {
                setCurrentView(month);
            }
        }, [month]);

        // Track hovered week for week mode highlighting
        const [hoveredWeekStart, setHoveredWeekStart] =
            React.useState<Date | null>(null);

        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        /**
         * Navigate to previous period based on mode
         */
        const handlePrevious = () => {
            setCurrentView((prev) => {
                switch (mode) {
                    case 'year':
                        return new Date(prev.getFullYear() - 12, 0, 1);
                    case 'quarter':
                    case 'month':
                        return new Date(prev.getFullYear() - 1, 0, 1);
                    default:
                        return new Date(
                            prev.getFullYear(),
                            prev.getMonth() - 1,
                            1,
                        );
                }
            });
        };

        /**
         * Navigate to next period based on mode
         */
        const handleNext = () => {
            setCurrentView((prev) => {
                switch (mode) {
                    case 'year':
                        return new Date(prev.getFullYear() + 12, 0, 1);
                    case 'quarter':
                    case 'month':
                        return new Date(prev.getFullYear() + 1, 0, 1);
                    default:
                        return new Date(
                            prev.getFullYear(),
                            prev.getMonth() + 1,
                            1,
                        );
                }
            });
        };

        /**
         * Handle day click - in week mode, selects the entire week
         */
        const handleDayClick = (day: number) => {
            const date = new Date(
                currentView.getFullYear(),
                currentView.getMonth(),
                day,
            );
            if (disabled && disabled(date)) return;

            if (mode === 'week') {
                // In week mode, pass both week start and end
                const weekStart = getWeekStart(date);
                const weekEnd = getWeekEnd(date);
                onSelect?.(weekStart, weekEnd);
            } else {
                onSelect?.(date);
            }
        };

        /**
         * Handle week hover for visual feedback
         */
        const handleWeekHover = (day: number) => {
            if (mode !== 'week') return;
            const date = new Date(
                currentView.getFullYear(),
                currentView.getMonth(),
                day,
            );
            const weekStart = getWeekStart(date);
            setHoveredWeekStart(weekStart);
        };

        /**
         * Check if a date is in the hovered week
         */
        const isInHoveredWeek = (date: Date) => {
            if (!hoveredWeekStart) return false;
            return isSameWeek(date, hoveredWeekStart);
        };

        /**
         * Handle month click
         */
        const handleMonthClick = (monthIndex: number) => {
            const date = new Date(currentView.getFullYear(), monthIndex, 1);
            if (disabled && disabled(date)) return;
            onSelect?.(date);
        };

        /**
         * Handle quarter click
         */
        const handleQuarterClick = (quarterIndex: number) => {
            const monthIndex = quarterIndex * 3;
            const date = new Date(currentView.getFullYear(), monthIndex, 1);
            if (disabled && disabled(date)) return;
            onSelect?.(date);
        };

        /**
         * Handle year click
         */
        const handleYearClick = (year: number) => {
            const date = new Date(year, 0, 1);
            if (disabled && disabled(date)) return;
            onSelect?.(date);
        };

        /**
         * Check if a month is in the selected range
         */
        const isMonthInRange = (monthIndex: number) => {
            if (!rangeStart || !rangeEnd) return false;
            const monthDate = new Date(
                currentView.getFullYear(),
                monthIndex,
                15,
            );
            return isInRange(monthDate, rangeStart, rangeEnd);
        };

        /**
         * Check if a month is range start
         */
        const isMonthRangeStart = (monthIndex: number) => {
            if (!rangeStart) return false;
            return (
                rangeStart.getFullYear() === currentView.getFullYear() &&
                rangeStart.getMonth() === monthIndex
            );
        };

        /**
         * Check if a month is range end
         */
        const isMonthRangeEnd = (monthIndex: number) => {
            if (!rangeEnd) return false;
            return (
                rangeEnd.getFullYear() === currentView.getFullYear() &&
                rangeEnd.getMonth() === monthIndex
            );
        };

        /**
         * Check if a quarter is in the selected range
         */
        const isQuarterInRange = (quarterIndex: number) => {
            if (!rangeStart || !rangeEnd) return false;
            const quarterStart = new Date(
                currentView.getFullYear(),
                quarterIndex * 3,
                15,
            );
            const quarterEnd = new Date(
                currentView.getFullYear(),
                quarterIndex * 3 + 2,
                15,
            );
            return (
                isInRange(quarterStart, rangeStart, rangeEnd) ||
                isInRange(quarterEnd, rangeStart, rangeEnd)
            );
        };

        /**
         * Check if a quarter is range start
         */
        const isQuarterRangeStart = (quarterIndex: number) => {
            if (!rangeStart) return false;
            return (
                rangeStart.getFullYear() === currentView.getFullYear() &&
                getQuarter(rangeStart.getMonth()) === quarterIndex + 1
            );
        };

        /**
         * Check if a quarter is range end
         */
        const isQuarterRangeEnd = (quarterIndex: number) => {
            if (!rangeEnd) return false;
            return (
                rangeEnd.getFullYear() === currentView.getFullYear() &&
                getQuarter(rangeEnd.getMonth()) === quarterIndex + 1
            );
        };

        /**
         * Check if a year is in the selected range
         */
        const isYearInRange = (year: number) => {
            if (!rangeStart || !rangeEnd) return false;
            return (
                year > rangeStart.getFullYear() && year < rangeEnd.getFullYear()
            );
        };

        /**
         * Check if a year is range start
         */
        const isYearRangeStart = (year: number) => {
            if (!rangeStart) return false;
            return rangeStart.getFullYear() === year;
        };

        /**
         * Check if a year is range end
         */
        const isYearRangeEnd = (year: number) => {
            if (!rangeEnd) return false;
            return rangeEnd.getFullYear() === year;
        };

        /**
         * Format header text based on mode
         */
        const formatHeaderText = () => {
            switch (mode) {
                case 'year': {
                    const startYear =
                        Math.floor(currentView.getFullYear() / 12) * 12;
                    return `${startYear} - ${startYear + 11}`;
                }
                case 'quarter':
                case 'month':
                    return currentView.getFullYear().toString();
                default:
                    return currentView.toLocaleDateString('en-US', {
                        month: 'long',
                        year: 'numeric',
                    });
            }
        };

        /**
         * Render day picker grid - styled to match Business Analytics DatePickerSlicer
         * In week mode, entire weeks are highlighted and selectable
         */
        const renderDayPicker = () => {
            const daysInMonth = getDaysInMonth(currentView);
            const firstDay = getFirstDayOfMonth(currentView);
            const weeks: React.ReactElement[][] = [];
            let currentWeek: React.ReactElement[] = [];
            const isWeekMode = mode === 'week';

            // Days from previous month to fill the first week
            const prevMonthDays = getDaysInMonth(
                new Date(currentView.getFullYear(), currentView.getMonth() - 1),
            );
            for (let i = 0; i < firstDay; i++) {
                const prevMonthDay = prevMonthDays - firstDay + i + 1;
                const placeholderDate = new Date(
                    currentView.getFullYear(),
                    currentView.getMonth() - 1,
                    prevMonthDay,
                );
                const inHoveredWeek =
                    isWeekMode && isInHoveredWeek(placeholderDate);
                const isDisabledDate = disabled && disabled(placeholderDate);

                // Week mode: check if this adjacent day is part of selected range
                const adjWeekStart = getWeekStart(placeholderDate);
                const adjWeekEnd = getWeekEnd(placeholderDate);
                const isAdjInSelectedWeekRange =
                    isWeekMode &&
                    rangeStart &&
                    rangeEnd &&
                    adjWeekStart.getTime() >= rangeStart.getTime() &&
                    adjWeekEnd.getTime() <= rangeEnd.getTime();
                const isAdjWeekStart =
                    isWeekMode &&
                    rangeStart &&
                    isSameDay(adjWeekStart, rangeStart);
                const isAdjWeekEnd =
                    isWeekMode && rangeEnd && isSameDay(adjWeekEnd, rangeEnd);
                const dayOfWeek = placeholderDate.getDay();

                currentWeek.push(
                    <button
                        key={`prev-${i}`}
                        type="button"
                        className={cn(
                            'flex h-8 w-8 items-center justify-center text-[0.82rem] text-gray-300',
                            'rounded-full hover:bg-gray-50 hover:text-gray-500',
                            // Hover styling (when not selected)
                            inHoveredWeek &&
                                !isAdjInSelectedWeekRange &&
                                'rounded-full bg-gray-100',
                            // Selected week range styling
                            isAdjInSelectedWeekRange &&
                                'rounded-full bg-[#e8e9eb]',
                            // Week boundaries for selected range
                            isWeekMode &&
                                dayOfWeek === 0 &&
                                (inHoveredWeek || isAdjInSelectedWeekRange) &&
                                'rounded-l-full',
                            isWeekMode &&
                                dayOfWeek === 6 &&
                                (inHoveredWeek || isAdjInSelectedWeekRange) &&
                                'rounded-r-full',
                            // Week start/end get black background
                            isAdjWeekStart &&
                                dayOfWeek === 0 &&
                                'rounded-l-full bg-black text-white',
                            isAdjWeekEnd &&
                                dayOfWeek === 6 &&
                                'rounded-r-full bg-black text-white',
                            isDisabledDate &&
                                'cursor-not-allowed opacity-50 hover:bg-transparent hover:text-gray-300',
                        )}
                        onClick={() => {
                            if (isDisabledDate) return;
                            // Select the date without navigating (it's already visible)
                            if (isWeekMode) {
                                const weekStart = getWeekStart(placeholderDate);
                                const weekEnd = getWeekEnd(placeholderDate);
                                onSelect?.(weekStart, weekEnd);
                            } else {
                                onSelect?.(placeholderDate);
                            }
                        }}
                        onMouseEnter={() => isWeekMode && handleWeekHover(1)}
                        disabled={isDisabledDate}
                    >
                        {prevMonthDay}
                    </button>,
                );
            }

            // Days of the month
            for (let day = 1; day <= daysInMonth; day++) {
                const date = new Date(
                    currentView.getFullYear(),
                    currentView.getMonth(),
                    day,
                );
                const isStart = isRangeStart(date, rangeStart);
                const isEnd = isRangeEnd(date, rangeEnd);
                const inRange = isInRange(date, rangeStart, rangeEnd);
                const isTodayDate = isToday(date);
                const isDisabled = disabled && disabled(date);
                const inHoveredWeek = isWeekMode && isInHoveredWeek(date);

                // In week mode, check if the entire week is selected
                const weekStart = getWeekStart(date);
                const weekEnd = getWeekEnd(date);
                const isWeekStart =
                    isWeekMode &&
                    rangeStart &&
                    isSameDay(weekStart, rangeStart);
                const isWeekEnd =
                    isWeekMode && rangeEnd && isSameDay(weekEnd, rangeEnd);
                const isInSelectedWeekRange =
                    isWeekMode &&
                    rangeStart &&
                    rangeEnd &&
                    weekStart.getTime() >= rangeStart.getTime() &&
                    weekEnd.getTime() <= rangeEnd.getTime();

                currentWeek.push(
                    <button
                        key={day}
                        type="button"
                        className={cn(
                            'flex h-8 w-8 items-center justify-center text-[0.82rem] transition-all',
                            // Week mode: highlight entire week on hover with rounded corners
                            isWeekMode &&
                                inHoveredWeek &&
                                !isInSelectedWeekRange &&
                                'rounded-full bg-gray-100',
                            // Day mode default state - rounded for hover
                            !isWeekMode && 'rounded-full',
                            // Day mode hover state - outline
                            !isWeekMode &&
                                !isStart &&
                                !isEnd &&
                                !isDisabled &&
                                'hover:bg-white hover:outline hover:outline-1 hover:outline-gray-300',
                            // Week mode: first/last day of week get rounded corners
                            isWeekMode &&
                                date.getDay() === 0 &&
                                (inHoveredWeek || isInSelectedWeekRange) &&
                                'rounded-l-full',
                            isWeekMode &&
                                date.getDay() === 6 &&
                                (inHoveredWeek || isInSelectedWeekRange) &&
                                'rounded-r-full',
                            // Range start/end styling - black circle (day mode) or black background (week mode)
                            !isWeekMode &&
                                (isStart || isEnd) &&
                                'rounded-full bg-black text-white hover:bg-black hover:outline-none',
                            // Week mode selected range styling - with rounded corners
                            isWeekMode &&
                                isInSelectedWeekRange &&
                                'rounded-full bg-[#e8e9eb]',
                            // Week mode: week boundaries get special styling
                            isWeekMode &&
                                isWeekStart &&
                                date.getDay() === 0 &&
                                'rounded-l-full bg-black text-white',
                            isWeekMode &&
                                isWeekEnd &&
                                date.getDay() === 6 &&
                                'rounded-r-full bg-black text-white',
                            // Day mode: In range styling - gray background with rounded corners
                            !isWeekMode &&
                                inRange &&
                                !isStart &&
                                !isEnd &&
                                'rounded-full bg-[#e8e9eb]',
                            // Today styling (when not selected)
                            isTodayDate &&
                                !isStart &&
                                !isEnd &&
                                !inRange &&
                                !isInSelectedWeekRange &&
                                'font-medium',
                            // Disabled styling
                            isDisabled &&
                                'cursor-not-allowed opacity-50 hover:bg-transparent hover:outline-none',
                            // Week mode cursor
                            isWeekMode && !isDisabled && 'cursor-pointer',
                        )}
                        onClick={() => handleDayClick(day)}
                        onMouseEnter={() => handleWeekHover(day)}
                        disabled={isDisabled}
                    >
                        {day}
                    </button>,
                );

                // Start new week after Saturday (7 days)
                if ((firstDay + day) % 7 === 0) {
                    weeks.push([...currentWeek]);
                    currentWeek = [];
                }
            }

            // Push remaining days with next month's days
            if (currentWeek.length > 0) {
                // Fill remaining cells in last week with next month's days
                const lastDay = daysInMonth;
                let nextMonthDay = 1;
                while (currentWeek.length < 7) {
                    const placeholderDate = new Date(
                        currentView.getFullYear(),
                        currentView.getMonth() + 1,
                        nextMonthDay,
                    );
                    const inHoveredWeek =
                        isWeekMode && isInHoveredWeek(placeholderDate);
                    const isDisabledDate =
                        disabled && disabled(placeholderDate);
                    const currentNextDay = nextMonthDay;

                    // Week mode: check if this adjacent day is part of selected range
                    const adjWeekStart = getWeekStart(placeholderDate);
                    const adjWeekEnd = getWeekEnd(placeholderDate);
                    const isAdjInSelectedWeekRange =
                        isWeekMode &&
                        rangeStart &&
                        rangeEnd &&
                        adjWeekStart.getTime() >= rangeStart.getTime() &&
                        adjWeekEnd.getTime() <= rangeEnd.getTime();
                    const isAdjWeekStart =
                        isWeekMode &&
                        rangeStart &&
                        isSameDay(adjWeekStart, rangeStart);
                    const isAdjWeekEnd =
                        isWeekMode &&
                        rangeEnd &&
                        isSameDay(adjWeekEnd, rangeEnd);
                    const dayOfWeek = placeholderDate.getDay();

                    currentWeek.push(
                        <button
                            key={`next-${nextMonthDay}`}
                            type="button"
                            className={cn(
                                'flex h-8 w-8 items-center justify-center text-[13px] text-gray-300',
                                'rounded-full hover:bg-gray-50 hover:text-gray-500',
                                // Hover styling (when not selected)
                                inHoveredWeek &&
                                    !isAdjInSelectedWeekRange &&
                                    'rounded-full bg-gray-100',
                                // Selected week range styling
                                isAdjInSelectedWeekRange &&
                                    'rounded-full bg-[#e8e9eb]',
                                // Week boundaries for selected range
                                isWeekMode &&
                                    dayOfWeek === 0 &&
                                    (inHoveredWeek ||
                                        isAdjInSelectedWeekRange) &&
                                    'rounded-l-full',
                                isWeekMode &&
                                    dayOfWeek === 6 &&
                                    (inHoveredWeek ||
                                        isAdjInSelectedWeekRange) &&
                                    'rounded-r-full',
                                // Week start/end get black background
                                isAdjWeekStart &&
                                    dayOfWeek === 0 &&
                                    'rounded-l-full bg-black text-white',
                                isAdjWeekEnd &&
                                    dayOfWeek === 6 &&
                                    'rounded-r-full bg-black text-white',
                                isDisabledDate &&
                                    'cursor-not-allowed opacity-50 hover:bg-transparent hover:text-gray-300',
                            )}
                            onClick={() => {
                                if (isDisabledDate) return;
                                // Select the date without navigating (it's already visible)
                                if (isWeekMode) {
                                    const weekStart =
                                        getWeekStart(placeholderDate);
                                    const weekEnd = getWeekEnd(placeholderDate);
                                    onSelect?.(weekStart, weekEnd);
                                } else {
                                    onSelect?.(placeholderDate);
                                }
                            }}
                            onMouseEnter={() =>
                                isWeekMode && handleWeekHover(lastDay)
                            }
                            disabled={isDisabledDate}
                        >
                            {currentNextDay}
                        </button>,
                    );
                    nextMonthDay++;
                }
                weeks.push(currentWeek);
            }

            return (
                <div
                    className="px-2"
                    onMouseLeave={() => setHoveredWeekStart(null)}
                >
                    {/* Day names header */}
                    <div className="mb-4 mt-2 flex justify-between">
                        {dayNames.map((day) => (
                            <div
                                key={day}
                                className="flex h-8 w-8 items-center justify-center text-[11px] text-gray-500"
                            >
                                {day}
                            </div>
                        ))}
                    </div>
                    {/* Week rows */}
                    {weeks.map((week, weekIndex) => (
                        <div
                            key={weekIndex}
                            className="mb-0.5 flex justify-between"
                        >
                            {week}
                        </div>
                    ))}
                </div>
            );
        };

        /**
         * Render month picker grid
         */
        const renderMonthPicker = () => {
            return (
                <div className="grid grid-cols-3 gap-2 p-4">
                    {MONTH_LABELS.map((label, index) => {
                        const isStart = isMonthRangeStart(index);
                        const isEnd = isMonthRangeEnd(index);
                        const inRange = isMonthInRange(index);
                        const monthDate = new Date(
                            currentView.getFullYear(),
                            index,
                            1,
                        );
                        const isDisabled = disabled && disabled(monthDate);

                        return (
                            <button
                                key={label}
                                type="button"
                                className={cn(
                                    'flex h-10 items-center justify-center rounded-full text-sm transition-colors',
                                    'hover:outline hover:outline-1 hover:outline-gray-300',
                                    (isStart || isEnd) &&
                                        'bg-black text-white hover:bg-black',
                                    inRange &&
                                        !isStart &&
                                        !isEnd &&
                                        'bg-[#e8e9eb]',
                                    isDisabled &&
                                        'cursor-not-allowed opacity-50 hover:outline-none',
                                )}
                                onClick={() => handleMonthClick(index)}
                                disabled={isDisabled}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>
            );
        };

        /**
         * Render quarter picker grid
         */
        const renderQuarterPicker = () => {
            return (
                <div className="grid grid-cols-2 gap-3 p-4">
                    {QUARTER_LABELS.map((label, index) => {
                        const isStart = isQuarterRangeStart(index);
                        const isEnd = isQuarterRangeEnd(index);
                        const inRange = isQuarterInRange(index);
                        const quarterDate = new Date(
                            currentView.getFullYear(),
                            index * 3,
                            1,
                        );
                        const isDisabled = disabled && disabled(quarterDate);

                        return (
                            <button
                                key={label}
                                type="button"
                                className={cn(
                                    'flex h-12 items-center justify-center rounded-full text-sm transition-colors',
                                    'hover:outline hover:outline-1 hover:outline-gray-300',
                                    (isStart || isEnd) &&
                                        'bg-black text-white hover:bg-black',
                                    inRange &&
                                        !isStart &&
                                        !isEnd &&
                                        'bg-[#e8e9eb]',
                                    isDisabled &&
                                        'cursor-not-allowed opacity-50 hover:outline-none',
                                )}
                                onClick={() => handleQuarterClick(index)}
                                disabled={isDisabled}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>
            );
        };

        /**
         * Render year picker grid
         */
        const renderYearPicker = () => {
            const startYear = Math.floor(currentView.getFullYear() / 12) * 12;
            const years = Array.from({ length: 12 }, (_, i) => startYear + i);

            return (
                <div className="grid grid-cols-3 gap-2 p-4">
                    {years.map((year) => {
                        const isStart = isYearRangeStart(year);
                        const isEnd = isYearRangeEnd(year);
                        const inRange = isYearInRange(year);
                        const yearDate = new Date(year, 0, 1);
                        const isDisabled = disabled && disabled(yearDate);

                        return (
                            <button
                                key={year}
                                type="button"
                                className={cn(
                                    'flex h-10 items-center justify-center rounded-full text-sm transition-colors',
                                    'hover:outline hover:outline-1 hover:outline-gray-300',
                                    (isStart || isEnd) &&
                                        'bg-black text-white hover:bg-black',
                                    inRange &&
                                        !isStart &&
                                        !isEnd &&
                                        'bg-[#e8e9eb]',
                                    isDisabled &&
                                        'cursor-not-allowed opacity-50 hover:outline-none',
                                )}
                                onClick={() => handleYearClick(year)}
                                disabled={isDisabled}
                            >
                                {year}
                            </button>
                        );
                    })}
                </div>
            );
        };

        /**
         * Render the picker content based on mode
         */
        const renderPickerContent = () => {
            switch (mode) {
                case 'month':
                    return renderMonthPicker();
                case 'quarter':
                    return renderQuarterPicker();
                case 'year':
                    return renderYearPicker();
                case 'week':
                case 'day':
                default:
                    return renderDayPicker();
            }
        };

        return (
            <div ref={ref} className={cn('w-[280px]', className)}>
                {/* Header - styled like Business Analytics */}
                <div className="my-2 flex items-center justify-between px-2">
                    <button
                        type="button"
                        onClick={handlePrevious}
                        className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    <h3 className="text-sm font-medium">
                        {formatHeaderText()}
                    </h3>
                    <button
                        type="button"
                        onClick={handleNext}
                        className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </div>

                {/* Picker content */}
                {renderPickerContent()}
            </div>
        );
    },
);

DateRangePicker.displayName = 'DateRangePicker';
